import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { X, Pencil, Trash2 } from "lucide-react";
import Button from "../common/Button";
import Loader from "../common/Loader";
import AddLocationModal from "./AddLocationModal";
import { getMyELQRs, deleteELQR } from "../../services/createEventService";
import { normalizeSavedLocation } from "../../utils/eventLocation";

/**
 * Saved-location picker, ported from the mobile app's
 * src/Screens/create-event-update/locations-bs/index.js.
 */
const SavedLocationsModal = ({ onClose, onConfirm }) => {
  const { t } = useTranslation(["events", "common"]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const fetchLocations = () => {
    setLoading(true);
    getMyELQRs(1, 30)
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.Result || [];
        setLocations(raw.map(normalizeSavedLocation));
      })
      .catch(() => toast.error(t("savedLocationsModal.toasts.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(fetchLocations, []);

  const handleDelete = async (location) => {
    if (!window.confirm(t("savedLocationsModal.confirmDelete", { name: location.LocationName }))) return;
    setDeletingId(location.Id);
    try {
      await deleteELQR(location.Id, location.Radius);
      setLocations((prev) => prev.filter((l) => l.Id !== location.Id));
      if (selectedId === location.Id) setSelectedId(null);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("savedLocationsModal.toasts.deleteFailed"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleConfirm = () => {
    const selected = locations.find((l) => l.Id === selectedId);
    if (!selected) return;
    onConfirm(selected);
    onClose();
  };

  if (showAddLocation) {
    return (
      <AddLocationModal
        locationObj={editingLocation}
        defaultName={t("savedLocationsModal.defaultLocationName", { index: locations.length + 1 })}
        onClose={() => {
          setShowAddLocation(false);
          setEditingLocation(null);
        }}
        onSaved={fetchLocations}
      />
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>{t("savedLocationsModal.title")}</h3>
          <button style={styles.closeBtn} onClick={onClose}><X size={16} strokeWidth={2} /></button>
        </div>

        {loading ? (
          <Loader />
        ) : locations.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.emptyText}>{t("savedLocationsModal.emptyText")}</p>
            <Button type="button" onClick={() => setShowAddLocation(true)}>
              {t("savedLocationsModal.addFirstLocation")}
            </Button>
          </div>
        ) : (
          <>
            <div style={styles.list}>
              {locations.map((loc) => (
                <div
                  key={loc.Id}
                  style={{ ...styles.row, ...(selectedId === loc.Id ? styles.rowSelected : {}) }}
                  onClick={() => setSelectedId(loc.Id)}
                >
                  <div style={styles.radio}>
                    {selectedId === loc.Id && <div style={styles.radioDot} />}
                  </div>
                  <div style={styles.rowBody}>
                    <div style={styles.rowName}>{loc.LocationName || t("savedLocationsModal.unnamedLocation")}</div>
                    <div style={styles.rowAddress}>{loc.Address}</div>
                    <div style={styles.rowMeta}>
                      {t("savedLocationsModal.radiusMeta", { radius: loc.Radius })}
                      {loc.IsGeofenceEnabled && <span style={styles.badge}>{t("savedLocationsModal.zoneBadge")}</span>}
                      {loc.LocationCheckInPoints.length > 0 && (
                        <span style={styles.badge}>{t("savedLocationsModal.checkInPinsBadge", { count: loc.LocationCheckInPoints.length })}</span>
                      )}
                    </div>
                  </div>
                  <div style={styles.rowActions}>
                    <button
                      type="button"
                      style={styles.iconBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingLocation(loc);
                        setShowAddLocation(true);
                      }}
                      title={t("common:actions.edit")}
                    >
                      <Pencil size={13} strokeWidth={2.25} />
                    </button>
                    <button
                      type="button"
                      style={styles.iconBtnDanger}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(loc);
                      }}
                      disabled={deletingId === loc.Id}
                      title={t("common:actions.delete")}
                    >
                      {deletingId === loc.Id ? "…" : <Trash2 size={13} strokeWidth={2.25} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" style={styles.addAnother} onClick={() => setShowAddLocation(true)}>
              {t("savedLocationsModal.addAnotherLocation")}
            </button>

            <div style={styles.actions}>
              <Button variant="secondary" type="button" onClick={onClose}>{t("common:actions.cancel")}</Button>
              <Button type="button" disabled={selectedId == null} onClick={handleConfirm}>
                {t("common:actions.confirm")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1150,
  padding: 16,
};

const modalStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
  width: "100%",
  maxWidth: 480,
  maxHeight: "85vh",
  overflowY: "auto",
  boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
};

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  closeBtn: { display: "flex", background: "none", border: "none", cursor: "pointer", color: "#888" },
  empty: { textAlign: "center", padding: "24px 0" },
  emptyText: { color: "#888", fontSize: 14, marginBottom: 14 },
  list: { display: "flex", flexDirection: "column", gap: 8, maxHeight: 340, overflowY: "auto" },
  row: {
    display: "flex", alignItems: "flex-start", gap: 10,
    padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e5e7eb", cursor: "pointer",
  },
  rowSelected: { borderColor: "#6c63ff", background: "#f4f3ff" },
  radio: {
    width: 18, height: 18, borderRadius: "50%", border: "2px solid #c7c9d9",
    flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center",
  },
  radioDot: { width: 9, height: 9, borderRadius: "50%", background: "#6c63ff" },
  rowBody: { flex: 1, minWidth: 0 },
  rowName: { fontWeight: 700, fontSize: 13, color: "#1e1e2d" },
  rowAddress: { fontSize: 12, color: "#666", marginTop: 2 },
  rowMeta: { display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 11, color: "#888" },
  badge: {
    background: "#ede9ff", color: "#6c63ff", fontWeight: 600,
    padding: "1px 7px", borderRadius: 10, fontSize: 10,
  },
  rowActions: { display: "flex", gap: 6, flexShrink: 0 },
  iconBtn: {
    width: 26, height: 26, borderRadius: 6, border: "1px solid #e5e7eb",
    background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  },
  iconBtnDanger: {
    width: 26, height: 26, borderRadius: 6, border: "1px solid #fecaca",
    background: "#fff", color: "#e53935", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  },
  addAnother: {
    marginTop: 12, background: "none", border: "none", color: "#6c63ff",
    fontWeight: 600, fontSize: 13, cursor: "pointer", padding: "6px 0",
  },
  actions: { display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 },
};

export default SavedLocationsModal;
